import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import FormularioAutoevaluacion from "./FormularioAutoevaluacion";
import FormularioEvaluacionSubordinado from "./FormularioEvaluacionSubordinado";

const EmpleadoDashboard = () => {
  const navigate = useNavigate();
  const [empleadoData, setEmpleadoData] = useState(null);
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard', 'autoevaluacion', 'evaluacion'
  const [selectedEvaluacion, setSelectedEvaluacion] = useState(null);
  const [evaluacionesAOtros, setEvaluacionesAOtros] = useState([]);
  const [autoevaluaciones, setAutoevaluaciones] = useState([]);

  useEffect(() => {
    // Obtener datos del empleado desde localStorage
    const empleadoSession = localStorage.getItem('empleadoSession');
    console.log('🔍 Datos de sesión del empleado:', empleadoSession);
    
    if (empleadoSession) {
      const data = JSON.parse(empleadoSession);
      console.log('👤 Datos del empleado parseados:', data);
      setEmpleadoData(data);
      loadMisEvaluaciones(data.cedula); // Usamos la nueva función unificada
    } else {
      console.log('❌ No hay datos de sesión del empleado');
      setLoading(false);
    }
  }, []);

  // Efecto adicional para recargar cuando cambie la vista activa
  useEffect(() => {
    if (activeView === 'dashboard' && empleadoData) {
      console.log('🔄 Vista dashboard activa, refrescando datos...');
      loadMisEvaluaciones(empleadoData.cedula);
    }
  }, [activeView, empleadoData]);

  const loadMisEvaluaciones = async (cedula) => {
    setLoading(true);
    try {
      console.log(`🔍 Buscando todas las evaluaciones relacionadas con la cédula: ${cedula}`);
      const processedIds = new Set(); // Para no procesar la misma evaluación dos veces

      // 1. Evaluaciones donde el empleado es el EVALUADOR
      const qSoyEvaluador = query(
        collection(db, "evaluaciones_asignadas"),
        where("evaluadorCedula", "==", cedula),
        where("activa", "==", true)
      );
      const soyEvaluadorSnapshot = await getDocs(qSoyEvaluador);
      const soyEvaluadorList = soyEvaluadorSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log(`📊 Encontradas ${soyEvaluadorList.length} evaluaciones donde el empleado es el evaluador.`);
      
      const autoevaluacionesList = [];
      const evaluacionesAOtrosList = [];

      soyEvaluadorList.forEach(ev => {
        if (processedIds.has(ev.id)) return;
        console.log(`📝 Procesando evaluación: ${ev.evaluacionNombre}, Estado: ${ev.estado}, Tipo: ${ev.tipoEvaluacion}`);
        
        // Verificar si es autoevaluación basado en el tipo y si el evaluado es el mismo
        if (ev.tipoEvaluacion === 'autoevaluacion' || ev.empleadoCedula === cedula) {
          autoevaluacionesList.push(ev);
        } else {
          // Si el evaluado es otra persona, es una evaluación a otro.
          evaluacionesAOtrosList.push(ev);
        }
        processedIds.add(ev.id);
      });

      // 2. Evaluaciones donde el empleado es el EVALUADO (para autoevaluaciones puras)
      const qMisEvaluaciones = query(
        collection(db, "evaluaciones_asignadas"),
        where("empleadoCedula", "==", cedula),
        where("activa", "==", true)
      );
      const misEvaluacionesSnapshot = await getDocs(qMisEvaluaciones);
      const misEvaluacionesList = misEvaluacionesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log(`📊 Encontradas ${misEvaluacionesList.length} evaluaciones donde el empleado es el evaluado.`);

      misEvaluacionesList.forEach(ev => {
        if (processedIds.has(ev.id)) return;
        console.log(`📝 Procesando evaluación como evaluado: ${ev.evaluacionNombre}, Estado: ${ev.estado}, Tipo: ${ev.tipoEvaluacion}`);
        
        // Es autoevaluación si el tipo es autoevaluacion o si no tiene evaluador o es el mismo empleado
        if (ev.tipoEvaluacion === 'autoevaluacion' || !ev.evaluadorCedula || ev.evaluadorCedula === cedula) {
          autoevaluacionesList.push(ev);
          processedIds.add(ev.id);
        }
        // Si el evaluador es otro, esa evaluación no es una tarea directa para el empleado logueado.
      });

      console.log('✅ Autoevaluaciones finales:', autoevaluacionesList.length);
      console.log('✅ Evaluaciones a otros finales:', evaluacionesAOtrosList.length);
      
      // Log de estados para debugging
      autoevaluacionesList.forEach(ev => {
        console.log(`🔍 Autoevaluación: ${ev.evaluacionNombre} - Estado: ${ev.estado} - Tipo: ${ev.tipoEvaluacion}`);
      });

      evaluacionesAOtrosList.forEach(ev => {
        console.log(`🔍 Evaluación a otro: ${ev.evaluacionNombre} - Evaluado: ${ev.empleadoNombre} - Estado: ${ev.estado} - Tipo: ${ev.tipoEvaluacion}`);
      });

      setAutoevaluaciones(autoevaluacionesList);
      setEvaluacionesAOtros(evaluacionesAOtrosList);

    } catch (error) {
      console.error("❌ Error al cargar las evaluaciones:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleIniciarEvaluacion = (evaluacion, tipo) => {
    console.log(`🚀 Iniciando evaluación de tipo '${tipo}':`, evaluacion);
    setSelectedEvaluacion(evaluacion);
    setActiveView(tipo);
  };

  const handleVolverDashboard = () => {
    setActiveView('dashboard');
    setSelectedEvaluacion(null);
    // Recargar datos para actualizar estados inmediatamente
    if (empleadoData) {
      console.log('🔄 Recargando evaluaciones después de completar...');
      loadMisEvaluaciones(empleadoData.cedula);
    }
  };

  const handleLogoutEmpleado = () => {
    console.log('🔒 Cerrando sesión del empleado...');
    
    // Limpiar localStorage completamente
    localStorage.removeItem('empleadoSession');
    localStorage.removeItem('lastLoginTime');
    localStorage.removeItem('userRole');
    
    // Limpiar estados del componente
    setEmpleadoData(null);
    setEvaluaciones([]);
    setEvaluacionesAOtros([]);
    setAutoevaluaciones([]);
    setSelectedEvaluacion(null);
    setActiveView('dashboard');
    
    // Redirigir al login usando React Router
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc'
      }}>
        <div style={{
          background: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #1e40af',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#64748b', margin: 0 }}>Cargando tu información...</p>
        </div>
      </div>
    );
  }

  // Renderizar componentes de evaluación según la vista activa
  if (activeView === 'autoevaluacion' && selectedEvaluacion) {
    return <FormularioAutoevaluacion evaluacion={selectedEvaluacion} onVolver={handleVolverDashboard} />;
  }

  if (activeView === 'evaluacion' && selectedEvaluacion) {
    return <FormularioEvaluacionSubordinado evaluacion={selectedEvaluacion} onVolver={handleVolverDashboard} />;
  }

  // Vista principal del dashboard del empleado
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header personalizado sin sidebar */}
      <header style={{
        backgroundColor: '#1e40af',
        color: 'white',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
            Panel de Empleado
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.875rem' }}>
            {empleadoData ? empleadoData.nombre : 'Empleado'} - {empleadoData ? (empleadoData.rol || 'Empleado') : ''}
          </span>
        </div>
      </header>

      {/* Contenido principal sin sidebar */}
      <main style={{ padding: '2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e293b', marginBottom: '1.5rem' }}>
            👋 ¡Hola, {empleadoData ? empleadoData.nombre : 'Empleado'}!
          </h2>

          {/* Sección de Autoevaluaciones */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
              Mis Autoevaluaciones
            </h3>
            {autoevaluaciones.length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '1rem' }}>
                {autoevaluaciones.map(evaluacion => (
                  <li key={evaluacion.id} style={{
                    backgroundColor: 'white',
                    padding: '1rem 1.5rem',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div>
                      <p style={{ fontWeight: '600', color: '#1e293b', margin: 0 }}>
                        {evaluacion.evaluacionNombre}
                      </p>
                      <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
                        Estado: <span style={{
                          fontWeight: '500',
                          color: evaluacion.estado === 'pendiente' ? '#f59e0b' : (evaluacion.estado === 'completada' ? '#10b981' : '#6b7280')
                        }}>
                          {evaluacion.estado}
                        </span>
                      </p>
                    </div>
                    <button
                      onClick={() => handleIniciarEvaluacion(evaluacion, 'autoevaluacion')}
                      style={{
                        backgroundColor: '#1e40af',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#1e3a8a'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#1e40af'}
                    >
                      {evaluacion.estado === 'completada' ? 'Ver' : 'Completar'}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#6b7280' }}>No tienes autoevaluaciones asignadas.</p>
            )}
          </div>

          {/* Sección de Evaluaciones a Otros */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
              Evaluaciones a Otros
            </h3>
            {evaluacionesAOtros.length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '1rem' }}>
                {evaluacionesAOtros.map(evaluacion => (
                  <li key={evaluacion.id} style={{
                    backgroundColor: 'white',
                    padding: '1rem 1.5rem',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div>
                      <p style={{ fontWeight: '600', color: '#1e293b', margin: 0 }}>
                        Evaluar a: {evaluacion.empleadoNombre}
                      </p>
                      <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
                        {evaluacion.evaluacionNombre}
                      </p>
                      <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
                        Estado: <span style={{
                          fontWeight: '500',
                          color: evaluacion.estado === 'pendiente' ? '#f59e0b' : (evaluacion.estado === 'completada' ? '#10b981' : '#6b7280')
                        }}>
                          {evaluacion.estado}
                        </span>
                      </p>
                    </div>
                    <button
                      onClick={() => handleIniciarEvaluacion(evaluacion, 'evaluacion')}
                      style={{
                        backgroundColor: '#1e40af',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#1e3a8a'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#1e40af'}
                    >
                      {evaluacion.estado === 'completada' ? 'Ver' : 'Evaluar'}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#6b7280' }}>No tienes evaluaciones asignadas para otros.</p>
            )}
          </div>

          {/* Botón de Logout */}
          <div style={{ marginTop: '2rem', borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', textAlign: 'center' }}>
            <button
              onClick={handleLogoutEmpleado}
              style={{
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '1rem',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmpleadoDashboard;
